import Papa from "papaparse";
import DownloadIcon from "@/assets/icons/download.svg?react";
import { IconButton } from "@/components/UI/TableToolbar/IconButton";
import { createDownloadFileName } from "../TableToolbar/utils";

type ClientDownloadButtonProps = {
  fileName: string;
  inert?: boolean;
} & (
  | { data?: object[]; fields?: never; rows?: never }
  | { data?: never; fields: string[]; rows: unknown[][] }
);

const DANGEROUS_CSV_PREFIX = /^[-=+@\t\r]/;

function sanitizeCsvCell<T>(value: T): T | string {
  if (typeof value !== "string") return value;
  return DANGEROUS_CSV_PREFIX.test(value) ? `'${value}` : value;
}

function sanitizeCsvRows(data: object[]) {
  return data.map((row) =>
    Object.fromEntries(
      Object.entries(row).map(([key, value]) => [key, sanitizeCsvCell(value)]),
    ),
  );
}

function sanitizeOrderedFields(fields: string[]) {
  return fields.map((field) => sanitizeCsvCell(field));
}

function sanitizeOrderedRows(rows: unknown[][]) {
  return rows.map((row) => row.map(sanitizeCsvCell));
}

export default function ClientDownloadButton({
  data,
  fields,
  rows,
  fileName,
  inert = false,
}: ClientDownloadButtonProps) {
  const hasRows = rows ? rows.length > 0 : Boolean(data?.length);

  const handleDownload = () => {
    if (inert || !hasRows) return;

    const csvInput = rows
      ? {
          fields: sanitizeOrderedFields(fields ?? []),
          data: sanitizeOrderedRows(rows),
        }
      : sanitizeCsvRows(data ?? []);
    // Pre-existing lint warning, surfaced only because this file is now staged. Kept as a
    // default-export call because the component's tests mock papaparse's default export;
    // switching to a named import would mean changing those tests too.
    // eslint-disable-next-line import-x/no-named-as-default-member
    const csv = Papa.unparse(csvInput);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${createDownloadFileName(fileName)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <IconButton
      data-testid="download-button"
      icon={<DownloadIcon />}
      title="Export data as CSV"
      onClick={handleDownload}
      disabled={!hasRows}
      /**
       * padding 0 is load-bearing: antd's default button padding is 15px a side, so a
       * 35px border-box button leaves 3px of content and the 20px icon renders as a dot.
       */
      style={{ height: "35px", width: "35px", padding: 0 }}
    />
  );
}
